<template>
  <div>
    <el-row :gutter="10" class="u-mb-10">
      <el-col :xs="24" :sm="8">
        <el-select v-model="provider" placeholder="Método de cálculo" style="width:100%"
                   @change="carregar" data-test="seletor-provider">
          <el-option v-for="p in providers" :key="p.chave"
                     :label="rotuloProvider(p)" :value="p.chave" :disabled="!p.disponivel"></el-option>
        </el-select>
      </el-col>
      <el-col :xs="24" :sm="8">
        <el-input v-model="instituicaoId" placeholder="Filtrar por escola (id)" size="small"
                  clearable @change="carregar"></el-input>
      </el-col>
      <el-col :xs="24" :sm="8" style="text-align:right">
        <el-button size="small" icon="el-icon-refresh" @click="carregar">ATUALIZAR</el-button>
        <el-button class="u-ml-10" size="small" icon="el-icon-location" :loading="geocodificando"
                   @click="geocodificar" data-test="btn-geocodificar">GEOCODIFICAR</el-button>
      </el-col>
    </el-row>

    <div v-if="carregando" class="estado" data-test="estado-carregando">
      <i class="el-icon-loading"></i> Calculando distâncias…
    </div>

    <el-alert v-else-if="erro" type="error" :closable="false" show-icon
              data-test="estado-erro" title="Não foi possível carregar o mapa" :description="erro"></el-alert>

    <template v-else>
      <!-- ============ IDENTIFICAÇÃO VISUAL DO MÉTODO (BI 3) ============ -->
      <el-alert :type="dados.houve_fallback ? 'warning' : 'info'" :closable="false" show-icon
                class="u-mb-10" data-test="legenda-metodo">
        <div slot="title">
          <b>{{ dados.tipo_distancia === 'viaria' ? 'Distância por TRAJETO viário' : 'Distância em LINHA RETA' }}</b>
          — método: {{ dados.provider }}
          <el-tag v-if="dados.houve_fallback" type="warning" size="mini" class="u-ml-5">
            fallback aplicado
          </el-tag>
        </div>
        <div class="legenda-texto">{{ dados.legenda }}</div>
      </el-alert>

      <!-- ============ alunos sem coordenada: contados, nunca omitidos ============ -->
      <el-alert v-if="totalNaoLocalizados > 0" type="warning" :closable="false" show-icon
                class="u-mb-10" data-test="nao-localizados">
        <div slot="title">
          {{ totalNaoLocalizados }} de {{ dados.total_analisados }} alunos não aparecem no mapa
        </div>
        <div class="legenda-texto">
          Sem coordenada do aluno: {{ dados.nao_localizados.aluno_sem_coordenada }} ·
          Sem coordenada da escola: {{ dados.nao_localizados.escola_sem_coordenada }}.
          Estes alunos <b>não</b> entram nas faixas de distância abaixo.
        </div>
      </el-alert>

      <el-alert v-if="!dados.total_pontos" type="info" :closable="false" show-icon
                data-test="estado-vazio"
                title="Nenhum aluno geocodificado"
                description="O mapa carregou normalmente, mas nenhum aluno tem coordenada registrada. Execute a geocodificação para visualizar."></el-alert>

      <template v-else>
        <el-row :gutter="10" class="u-mb-10">
          <el-col :xs="12" :sm="6" v-for="(qtd, faixa) in dados.faixas" :key="faixa">
            <div class="cartao">
              <div class="cartao-rotulo">{{ faixa }}</div>
              <div class="cartao-valor">{{ qtd }}</div>
            </div>
          </el-col>
        </el-row>

        <GmapMap v-if="centro" :center="centro" :zoom="13" style="width:100%;height:420px"
                 data-test="mapa">
          <GmapMarker v-for="(p, i) in pontosLimitados" :key="'a' + i"
                      :position="{ lat: p.aluno_lat, lng: p.aluno_lng }"
                      :title="p.aluno + ' — ' + formatarDistancia(p.metros)"></GmapMarker>
          <GmapMarker v-for="(e, i) in escolas" :key="'e' + i"
                      :position="{ lat: e.lat, lng: e.lng }" :title="e.nome"
                      :icon="iconeEscola"></GmapMarker>
          <GmapPolyline v-for="(p, i) in pontosLimitados" :key="'l' + i"
                        :path="[{ lat: p.aluno_lat, lng: p.aluno_lng },
                                { lat: p.escola_lat, lng: p.escola_lng }]"
                        :options="{ strokeColor: '#409EFF', strokeWeight: 1, strokeOpacity: 0.5 }"></GmapPolyline>
        </GmapMap>

        <c-panel title="Maiores distâncias" titleIcon="fas fa-route" class="u-mt-10">
          <table class="table table-striped" data-test="tabela-extremos">
            <thead>
              <tr><th>Aluno</th><th>Bairro</th><th>Escola</th><th>Distância</th>
                  <th>Precisão</th><th v-if="temDuracao">Tempo</th></tr>
            </thead>
            <tbody>
              <tr v-for="(p, i) in dados.extremos" :key="i">
                <td>{{ p.aluno }}</td>
                <td>{{ p.bairro || '—' }}</td>
                <td>{{ p.escola }}</td>
                <td>{{ formatarDistancia(p.metros) }}</td>
                <td><el-tag size="mini" :type="p.precisao === 'exato' ? 'success' : 'info'">
                  {{ p.precisao || 'não informada' }}</el-tag></td>
                <td v-if="temDuracao">{{ p.duracao_s ? Math.round(p.duracao_s / 60) + ' min' : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </c-panel>
      </template>
    </template>
  </div>
</template>

<script>
/**
 * FASE 5 / BLOCO BA-4 — BI 3.
 *
 * Mapa aluno → escola.
 *
 * Três exigências do edital que a tela cumpre explicitamente:
 *
 * 1. A distância é APRESENTADA, com o método identificado. A legenda diz se é linha reta ou
 *    trajeto e qual provider respondeu — e avisa quando houve fallback. Sem isso, 2 km em
 *    linha reta e 2 km de rua pareceriam a mesma coisa para quem leva a criança à escola.
 * 2. Aluno sem coordenada é CONTADO em destaque, com a razão. Um mapa que mostra 40 de 71
 *    sem dizer que faltam 31 induz o gestor ao erro.
 * 3. "Nenhum aluno geocodificado" é um estado distinto de "erro ao carregar".
 */
import biApi from '../biApi'

export default {
  data () {
    return {
      api: null,
      providers: [],
      provider: null,
      instituicaoId: '',
      dados: { faixas: {}, nao_localizados: {}, extremos: [], pontos: [] },
      carregando: false,
      geocodificando: false,
      erro: null,
      iconeEscola: {
        path: 'M -8,-8 8,-8 8,8 -8,8 z',
        fillColor: '#E6A23C', fillOpacity: 1, strokeWeight: 1, scale: 1
      }
    }
  },

  computed: {
    totalNaoLocalizados () {
      const n = this.dados.nao_localizados || {}
      return (n.aluno_sem_coordenada || 0) + (n.escola_sem_coordenada || 0)
    },
    // O mapa desenha no máximo 300 marcadores: acima disso o navegador trava e o gestor
    // não distingue nada mesmo. A tabela de extremos continua completa.
    pontosLimitados () { return (this.dados.pontos || []).slice(0, 300) },
    centro () {
      const p = this.pontosLimitados[0]
      return p ? { lat: p.aluno_lat, lng: p.aluno_lng } : null
    },
    escolas () {
      const vistas = {}
      const out = []
      ;(this.dados.pontos || []).forEach(function (p) {
        const k = p.escola_lat + ',' + p.escola_lng
        if (vistas[k]) return
        vistas[k] = true
        out.push({ nome: p.escola, lat: p.escola_lat, lng: p.escola_lng })
      })
      return out
    },
    temDuracao () {
      return (this.dados.extremos || []).some(function (p) { return p.duracao_s })
    }
  },

  mounted () {
    this.api = biApi.criar({ axios: this.$axios, base: this.base(), auth: this.auth() })
    this.carregarProviders()
  },

  methods: {
    base () { return this.$store.state.config.baseUrlApi },
    auth () { return this.$store.getters.getHeaderAuth },

    /**
     * Normaliza o erro que a API devolve.
     *
     * ARMADILHA REAL deste backend: negativa de permissão vem com HTTP **200** e corpo
     * `{erro: {cod: 800, text: '...'}}` — não com 403. Tratar só `status === 403` nunca
     * dispararia, e atribuir `d.erro` direto renderizaria "[object Object]" na tela.
     * O interceptor global de `main.js` já redireciona 800/801 para `/not-permission`;
     * isto cobre os demais casos e o texto exibido.
     */
    textoErro (erro) {
      if (!erro) return null
      if (typeof erro === 'string') return erro
      if (erro.text) return erro.text
      if (erro.message) return erro.message
      return 'Não foi possível concluir a operação.'
    },

    mensagemErro (e) {
      const r = e && e.response ? e.response : null
      if (!r) return 'Falha de comunicação com o servidor.'
      if (r.status === 403) return 'Você não tem autorização para este mapa.'
      if (r.data && r.data.erro) return this.textoErro(r.data.erro)
      return 'Erro ' + r.status + ' ao consultar o servidor.'
    },

    rotuloProvider (p) {
      const tipo = p.tipo === 'viaria' ? 'trajeto viário' : 'linha reta'
      return p.chave + ' (' + tipo + ')' + (p.disponivel ? '' : ' — indisponível')
    },

    carregarProviders () {
      const self = this
      this.api.providersDistancia().then(function (r) {
        self.providers = r.data || []
        const disp = self.providers.filter(function (p) { return p.disponivel })
        self.provider = disp.length ? disp[0].chave : null
        self.carregar()
      }).catch(function (e) {
        self.erro = self.mensagemErro(e)
      })
    },

    carregar () {
      const self = this
      this.carregando = true
      this.erro = null

      const filtros = { provider: this.provider }
      if (this.instituicaoId) filtros.instituicao_id = this.instituicaoId

      this.api.mapa(filtros).then(function (r) {
        self.carregando = false
        const d = r.data || {}
        if (d.erro) { self.erro = self.textoErro(d.erro); return }
        self.dados = d
      }).catch(function (e) {
        self.carregando = false
        self.erro = self.mensagemErro(e)
      })
    },

    /** Dispara a geocodificação em lote — evita que o operador precise de terminal. */
    geocodificar () {
      if (this.geocodificando) return
      const self = this
      this.geocodificando = true
      this.$axios.post(this.base() + '/api/bi-geo/geocodificar-lote', { limite: 200 }, this.auth())
        .then(function (r) {
          self.geocodificando = false
          const d = r.data || {}
          window.eventBus.$emit('alert', {
            msg: d.aviso ? d.aviso : ('Candidatos processados: ' + (d.candidatos || 0)),
            type: d.aviso ? 'warning' : 'success'
          })
          self.carregar()
        }).catch(function (e) {
          self.geocodificando = false
          window.eventBus.$emit('alert', { msg: self.mensagemErro(e), type: 'error' })
        })
    },

    formatarDistancia (m) {
      if (m === null || m === undefined) return '—'
      return m < 1000 ? m + ' m' : (m / 1000).toFixed(2) + ' km'
    }
  }
}
</script>

<style scoped>
.estado { padding: 30px; text-align: center; color: #909399; }
.cartao { background: #f5f7fa; border-radius: 4px; padding: 10px; text-align: center; }
.cartao-rotulo { font-size: 12px; color: #909399; }
.cartao-valor { font-size: 22px; font-weight: 600; }
.legenda-texto { font-size: 12px; }
</style>
